<script>
  // Part C (rules-editor redesign): the wide, URL-addressable rules edit page
  // (/generations/:id/rules). Hosts the create/edit form RELOCATED out of the
  // cramped 288px rail (RuleEditor.svelte), same /api/generations/:id/rules
  // CRUD. B-core extended this form from 8 to 14 real rule_types (time_realism
  // later made 15 — see RULE_LABELS in utils/rules.js), wired
  // category/product selection to name-based pickers (CategoryPicker /
  // AllowListPicker) everywhere, went one-field-per-row with help text, and
  // made the rule-type description render prominently above the fields. The
  // rail keeps its own read-only Part A "rules in words" list; this page also
  // shows that same read-only list (reused rendering) alongside the wide form
  // so a create/edit here is visible without leaving the page.
  import { onMount } from "svelte";
  import { apiRequest } from "../utils/api";
  import { RULE_LABELS, RULE_DESCRIPTIONS, DEFAULT_PARAMS, rulePhase, ruleLabel, ruleDescription } from "../utils/rules";
  import AllowListPicker from "./AllowListPicker.svelte";
  import CategoryPicker from "./CategoryPicker.svelte";
  import { readClipboard, writeClipboard, clearClipboard } from "../utils/rulesClipboard";

  export let generationId = null;
  export let isAdmin = false;
  export let onClose = () => {}; // ask AppShell to navigate back to /generations/:id
  // URL-owned create/edit sub-mode (AppShell drives these from the address bar:
  // .../rules/new and .../rules/:ruleId/edit). The form is no longer internal
  // state — opening/closing it is a real navigation so Back closes it and each
  // deep-links / survives an F5.
  export let formMode = null;   // null | "new" | "edit"
  export let editRuleId = null; // number | null
  export let onOpenNew = () => {};       // -> AppShell pushes .../rules/new
  export let onOpenEdit = (_ruleId) => {}; // -> AppShell pushes .../rules/:id/edit
  export let onCloseForm = () => {};     // -> AppShell pushes .../rules

  let generation = null;
  let loadingGeneration = true;
  let generationError = null;

  let rules = [];
  let loading = true;
  let error = null;

  let showRawIds = new Set();

  // Always-on defaults have NO id (they are not rows), so keying the raw-JSON
  // toggle on `rule.id` alone would give all of them the same key `null` and
  // expand every one at once. rule_type is what makes a default unique.
  function ruleKey(rule) {
    return rule.id ?? `default:${rule.rule_type}`;
  }

  function toggleRaw(ruleId) {
    const next = new Set(showRawIds);
    if (next.has(ruleId)) {
      next.delete(ruleId);
    } else {
      next.add(ruleId);
    }
    showRawIds = next;
  }

  // Always-on entries pin to the top regardless of the order the API returned
  // them in: they are the baseline every generation starts from, so they read as
  // context for the rows underneath rather than as peers of them.
  $: defaultRules = rules.filter((rule) => rule.source === "default");
  $: ownRules = rules.filter((rule) => rule.source !== "default");

  // Rules CLIPBOARD (copy here, paste on another generation's rules page).
  //
  // Only this generation's OWN rows are copyable. Project rules belong to the
  // project — copying them down would fork a shared setting into a private one
  // behind the operator's back — and the always-on baseline has no row to copy
  // (`id: null`); pasting it would freeze a tracked default into a fixed value.
  $: copyableRules = rules.filter((rule) => rule.source === "generation");

  let clipboard = readClipboard();
  let pasting = false;

  // Both sides are normalised: the buffer round-trips through JSON while the
  // prop comes from the URL parser, so a raw !== would call a generation
  // "another generation" purely over string-vs-number.
  const sameId = (a, b) => a != null && b != null && Number(a) === Number(b);

  // The banner only makes sense on a DIFFERENT generation than the one copied.
  $: clipboardIsElsewhere = clipboard != null && !sameId(clipboard.generationId, generationId);
  // Paste is same-project only (rule params address catalog categories by
  // project-local id — the server enforces this too). Until the generation has
  // loaded we don't know its project, so we don't yet claim a mismatch.
  $: clipboardCrossProject =
    clipboardIsElsewhere && generation != null && !sameId(clipboard.projectId, generation.project_id);

  function copyRules() {
    clipboard = writeClipboard({
      generationId: Number(generationId),
      generationName: generation?.name ?? null,
      projectId: generation?.project_id ?? null,
      ruleCount: copyableRules.length
    });
    saveError = null;
    saveMessage = `Copied ${copyableRules.length} rule${copyableRules.length === 1 ? "" : "s"} to the clipboard`;
  }

  function dismissClipboard() {
    clearClipboard();
    clipboard = null;
  }

  async function pasteRules(mode) {
    if (pasting || !clipboard) return;
    if (
      mode === "replace" &&
      !confirm(
        `Replace this generation's ${copyableRules.length} rule(s) with the rules from generation #${clipboard.generationId}?`
      )
    ) {
      return;
    }

    pasting = true;
    saveError = null;
    saveMessage = null;
    try {
      const result = await apiRequest(`/api/generations/${generationId}/rules/paste`, {
        method: "POST",
        body: { source_generation_id: clipboard.generationId, mode }
      });
      await loadRules();
      saveMessage = pasteSummary(result);
    } catch (e) {
      saveError = e?.message || "Could not paste rules";
    } finally {
      pasting = false;
    }
  }

  // Report what the paste actually did rather than a flat "pasted": append
  // overwrites a named policy that already existed and skips an exact duplicate,
  // so "5 rules" and "3 added, 1 updated, 1 already here" are different stories.
  function pasteSummary(result) {
    const parts = [];
    if (result?.created) parts.push(`${result.created} added`);
    if (result?.updated) parts.push(`${result.updated} updated`);
    if (result?.skipped) parts.push(`${result.skipped} already identical`);
    const detail = parts.length ? parts.join(", ") : "nothing to paste";
    return `Pasted from generation #${result?.source_generation_id ?? clipboard?.generationId}: ${detail}`;
  }

  // Collapsed by default: seven full cards of baseline pushed the generation's
  // OWN rules — the thing the operator came here to work on — below the fold.
  // The header still states they exist and how many, which is the whole point of
  // showing them at all; the detail is one click away.
  let showDefaults = false;

  let editingRuleType = "allow_list";
  let editingParams = {};
  let saving = false;
  let saveError = null;
  let saveMessage = null;

  // Whether the rule-type currently shown embeds a category/product picker
  // (CategoryPicker / AllowListPicker), and whether that picker has finished
  // its async load. The picker box grows from a one-line "Loading…" state to
  // the full checkbox list once its fetches resolve; while that's in flight the
  // Save/Create button would move under a click and swallow it. We disable Save
  // until the picker reports ready (via its `ready` event) so a save can't be
  // attempted — or silently dropped — against a still-loading picker.
  const PICKER_RULE_TYPES = new Set([
    "allow_list", "anchor_first", "min_gap_after", "no_consecutive",
    "max_count", "ratio", "followed_by", "section_order", "category_consistency"
  ]);
  $: formHasPicker = PICKER_RULE_TYPES.has(editingRuleType);
  let pickerReady = false;
  function onPickerReady(ready) {
    // A form may host more than one picker (followed_by's From/To). Any picker
    // reporting ready flips this true; that's sufficient because they load in
    // parallel off the same project and the button only needs the box to have
    // settled its height. Reset to false whenever the shown type changes below.
    if (ready) pickerReady = true;
  }
  // Re-arm the gate every time the picker-bearing type changes (or the form
  // (re)opens onto one) so switching rule types can't leave a stale "ready".
  $: { editingRuleType; formMode; editRuleId; pickerReady = false; }

  const RULE_TYPES = Object.keys(RULE_LABELS);

  // The form is shown/populated purely from the URL-owned props above.
  //   formMode "new"           -> blank add form
  //   formMode "edit"+editRuleId -> populated from the loaded rule
  // editingRuleId drives POST-vs-PATCH and the "Add rule" vs "Edit rule" labels.
  $: showAddForm = formMode === "new" || formMode === "edit";
  $: editingRuleId = formMode === "edit" ? editRuleId : null;

  // Initialise editingRuleType/editingParams ONCE per opened form instance
  // (keyed by a signature), so a reactive re-run doesn't clobber user input.
  // For "edit" we wait until `rules` has loaded (deep-link/F5 lands here before
  // the fetch resolves) and the target rule is present, then hydrate from it.
  let formInitSig = null;
  $: initForm(formMode, editRuleId, rules, loading);
  function initForm(mode, rid, loadedRules, isLoading) {
    if (!mode) { formInitSig = null; return; }
    if (mode === "new") {
      if (formInitSig !== "new") {
        editingRuleType = "allow_list";
        editingParams = { ...DEFAULT_PARAMS["allow_list"] };
        saveError = null;
        saveMessage = null;
        formInitSig = "new";
      }
      return;
    }
    if (mode === "edit" && rid) {
      const sig = `edit:${rid}`;
      if (formInitSig === sig) return;
      const rule = (loadedRules || []).find((r) => r.id === rid && r.source === "generation");
      if (rule) {
        editingRuleType = rule.rule_type;
        editingParams = JSON.parse(JSON.stringify(rule.params));
        saveError = null;
        saveMessage = null;
        formInitSig = sig;
        return;
      }
      // Rules have finished loading but this id isn't an editable generation
      // rule (project-source, deleted, or a hand-typed / stale edit URL). Don't
      // strand a phantom blank "Edit rule" form — navigate back to the list.
      // Only decide once loading is done, so a valid deep-link that resolves
      // AFTER mount still gets its chance to hydrate above.
      if (!isLoading) {
        onCloseForm();
      }
    }
  }

  onMount(async () => {
    await Promise.all([loadGeneration(), loadRules()]);
  });

  async function loadGeneration() {
    if (!generationId) return;
    loadingGeneration = true;
    generationError = null;
    try {
      generation = await apiRequest(`/api/generations/${generationId}`);
    } catch (e) {
      generationError = e?.message || "Could not load generation";
      generation = null;
    } finally {
      loadingGeneration = false;
    }
  }

  async function loadRules() {
    if (!generationId) return;
    loading = true;
    error = null;
    try {
      rules = await apiRequest(`/api/generations/${generationId}/rules`);
    } catch (e) {
      error = e?.message || "Could not load rules";
    } finally {
      loading = false;
    }
  }

  // Opening/closing the form is a navigation now (AppShell owns the URL); the
  // actual field population happens in initForm() reacting to the new props.
  function startAdd() {
    onOpenNew();
  }

  function startEdit(rule) {
    onOpenEdit(rule.id);
  }

  function cancelEdit() {
    onCloseForm();
  }

  function onRuleTypeChange() {
    editingParams = JSON.parse(JSON.stringify(DEFAULT_PARAMS[editingRuleType]));
  }

  async function saveRule() {
    if (saving) return;
    saving = true;
    saveError = null;
    saveMessage = null;

    const body = {
      rule: {
        rule_type: editingRuleType,
        params: editingParams,
        // NOTE: rule-level `scope` is deliberately NOT sent. Rules::Engine
        // reads it for zero rule types (allow_list keys off each entry's own
        // section/product type; max_count has its own params.scope; the rest
        // ignore it) — the column is only echoed into guardrail metadata. On
        // edit the controller leaves the stored scope untouched (absent key).
        //
        // B-core: the 5 completion-phase task-requirement types (plus
        // section_order, already completion) must persist with phase
        // "completion" or TaskRequirements/GenerationFinalValidator's
        // intent is misrepresented even though lookup itself is by
        // rule_type alone. Everything else (the transaction-phase engine
        // primitives + category_consistency) persists "transaction".
        phase: rulePhase(editingRuleType)
      }
    };

    const isEdit = formMode === "edit" && editRuleId;
    try {
      if (isEdit) {
        await apiRequest(`/api/generations/${generationId}/rules/${editRuleId}`, {
          method: "PATCH",
          body
        });
      } else {
        await apiRequest(`/api/generations/${generationId}/rules`, {
          method: "POST",
          body
        });
      }
      await loadRules();
      // Close the form (navigate back to .../rules); the success message shows
      // above the now-collapsed form. formInitSig is cleared on close so the
      // next open re-initialises cleanly.
      onCloseForm();
      saveMessage = isEdit ? "Rule updated" : "Rule created";
    } catch (e) {
      saveError = e?.message || "Could not save rule";
    } finally {
      saving = false;
    }
  }

  async function deleteRule(rule) {
    if (!confirm(`Delete rule "${ruleLabel(rule)}"?`)) return;
    try {
      await apiRequest(`/api/generations/${generationId}/rules/${rule.id}`, { method: "DELETE" });
      saveMessage = "Rule deleted";
      await loadRules();
    } catch (e) {
      saveError = e?.message || "Could not delete rule";
    }
  }

  function onParamsChange(key, value) {
    editingParams = { ...editingParams, [key]: value };
  }
</script>

<div data-testid="rules-page">
  <button type="button" class="btn-secondary mb-4" data-testid="rules-page-back" on:click={onClose}>
    ← Back to generation
  </button>

  <div class="flex items-center justify-between gap-4 mb-5 flex-wrap">
    <h2 class="text-base font-bold text-neutral-800">
      Rules
      {#if generation}
        <span class="text-neutral-400 font-normal">— {generation.name}</span>
      {/if}
    </h2>

    <!-- Copy is admin-only like paste: a non-admin filling the clipboard could
         never empty it (the paste endpoint is admin-gated), so offering it would
         be a dead end.

         Gated on the GENERATION being loaded too, not just the rules: the buffer
         records which project it came from, and that only arrives with the
         generation fetch. Copying before it lands would store projectId null,
         which matches no project — the buffer would then refuse to paste
         anywhere, with the banner giving no hint why. -->
    {#if isAdmin}
      <button
        type="button"
        class="btn-secondary"
        data-testid="rules-copy"
        disabled={loading || loadingGeneration || !generation || copyableRules.length === 0}
        title={copyableRules.length === 0
          ? "This generation has no rules of its own to copy"
          : "Copy this generation's own rules, to paste onto another generation"}
        on:click={copyRules}
      >
        Copy rules{copyableRules.length ? ` (${copyableRules.length})` : ""}
      </button>
    {/if}
  </div>

  {#if loadingGeneration}
    <p class="text-xs text-neutral-500 mb-3">Loading generation…</p>
  {:else if generationError}
    <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs mb-3" role="alert">{generationError}</p>
  {/if}

  {#if !isAdmin}
    <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs" role="alert" data-testid="rules-page-forbidden">
      Only an admin can create or edit rules. You can still review the current rules below.
    </p>
  {/if}

  <!-- Clipboard banner: shown on every OTHER generation's rules page once
       something has been copied, until it is pasted away or dismissed (it
       survives navigation, which is the whole point — carry rules from one
       generation to the next). -->
  {#if isAdmin && clipboardIsElsewhere}
    <div
      class="bg-info-bg text-info-text border border-info-border/30 rounded px-3 py-2 text-xs mt-3 flex items-center justify-between gap-3 flex-wrap"
      data-testid="rules-clipboard-banner"
    >
      <span>
        Copied {clipboard.ruleCount} rule{clipboard.ruleCount === 1 ? "" : "s"} from generation
        <strong>#{clipboard.generationId}</strong>{#if clipboard.generationName}&nbsp;({clipboard.generationName}){/if}
        {#if clipboardCrossProject}
          <br />
          <span class="text-warning-text" data-testid="rules-clipboard-cross-project">
            Can't paste here: rules address catalog categories by id, and those ids belong to the
            project they were written in. Paste is limited to generations of the same project.
          </span>
        {/if}
      </span>
      <span class="flex items-center gap-2 shrink-0">
        <button
          type="button"
          class="btn-secondary"
          data-testid="rules-clipboard-append"
          disabled={pasting || clipboardCrossProject}
          title="Add the copied rules to the ones already here"
          on:click={() => pasteRules("append")}
        >
          Paste alongside
        </button>
        <button
          type="button"
          class="btn-danger"
          data-testid="rules-clipboard-replace"
          disabled={pasting || clipboardCrossProject}
          title="Delete this generation's own rules, then paste the copied ones"
          on:click={() => pasteRules("replace")}
        >
          Replace
        </button>
        <button
          type="button"
          class="btn-secondary"
          data-testid="rules-clipboard-dismiss"
          disabled={pasting}
          on:click={dismissClipboard}
        >
          Dismiss
        </button>
      </span>
    </div>
  {/if}

  <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start mt-4">
    <!-- LEFT (wide): create/edit form, admin-only. Vertical layout, full-width
         fields — the whole point of Part C is room to breathe vs. the 288px rail. -->
    <div class="lg:col-span-3 min-w-0 space-y-4">
      {#if isAdmin}
        {#if saveMessage}
          <p class="bg-success-bg text-success-text border border-success-border/20 rounded px-2.5 py-1 text-xs">{saveMessage}</p>
        {/if}
        {#if saveError}
          <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs" role="alert">{saveError}</p>
        {/if}

        {#if !showAddForm}
          <button type="button" class="btn-primary" data-testid="add-rule" on:click={startAdd}>Add rule</button>
        {:else}
          <div class="bg-neutral-50 border border-neutral-200 rounded-lg p-5 space-y-4">
            <h4 class="text-sm font-bold text-neutral-800 tracking-wide">{editingRuleId ? "Edit rule" : "Add rule"}</h4>

            <!-- Rule type select: full width, own row. Its description renders
                 PROMINENTLY below (larger, its own callout block) rather than a
                 tiny caption — the point of goal #4. -->
            <div class="form-group">
              <label class="form-label" for="rule-type-select">Rule Type</label>
              <select id="rule-type-select" class="form-input" bind:value={editingRuleType} on:change={onRuleTypeChange} data-testid="rule-type">
                {#each RULE_TYPES as type}
                  <option value={type}>{RULE_LABELS[type]}</option>
                {/each}
              </select>
            </div>

            {#if RULE_DESCRIPTIONS[editingRuleType]}
              <div class="bg-info-bg border border-info-border/30 rounded-md px-3 py-2.5" data-testid="rule-type-description">
                <p class="text-sm text-info-text font-medium leading-snug">{RULE_DESCRIPTIONS[editingRuleType]}</p>
              </div>
            {/if}

            <!-- Per-type fields: ONE field per row (goal #3), each with a help
                 line explaining what it really does and when it cuts. Category/
                 product selection is always a name-based picker (goal #2) —
                 never a bare id input or comma-separated-ids text field. -->
            {#if editingRuleType === "allow_list"}
              <div class="form-group">
                <span class="form-label">Allowed Products / Categories</span>
                <AllowListPicker
                  {generationId}
                  selected={editingParams.entries || []}
                  on:change={({ detail }) => editingParams = { ...editingParams, entries: detail }}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  Any spending row outside this list is rejected at post time. You can mix whole categories and individual products in the same list.
                </p>
              </div>
            {:else if editingRuleType === "anchor_first"}
              <div class="form-group">
                <span class="form-label">Category</span>
                <CategoryPicker
                  {generationId}
                  mode="single"
                  selected={editingParams.category ?? null}
                  on:change={({ detail }) => onParamsChange("category", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  The first spending row the generation posts must be in this category. Every other row is unaffected.
                </p>
              </div>
            {:else if editingRuleType === "min_gap_after"}
              <div class="form-group">
                <span class="form-label">Category</span>
                <CategoryPicker
                  {generationId}
                  mode="single"
                  selected={editingParams.category ?? null}
                  on:change={({ detail }) => onParamsChange("category", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  The category that starts the cooldown — a row here starts the clock for the "Minutes" field below.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-min">Minutes</label>
                <input id="rule-param-min" class="form-input" type="number" min="1" bind:value={editingParams.minutes} />
                <p class="text-xs text-neutral-500 mt-1">
                  After a row in the category above, the next row (any category) must be posted at least this many minutes later.
                </p>
              </div>
            {:else if editingRuleType === "no_consecutive"}
              <div class="form-group">
                <span class="form-label">Categories</span>
                <CategoryPicker
                  {generationId}
                  mode="multi"
                  selected={editingParams.categories || []}
                  on:change={({ detail }) => onParamsChange("categories", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  These are the categories the "no consecutive" check watches. Order doesn't matter here.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-m">Mode</label>
                <select id="rule-param-m" class="form-input" bind:value={editingParams.mode} data-testid="rule-param-mode">
                  <option value="duplicate">duplicate</option>
                  <option value="any">any</option>
                </select>
                <p class="text-xs text-neutral-500 mt-1">
                  "duplicate" blocks the exact same product twice in a row; "any" blocks any two consecutive rows both landing in the selected categories.
                </p>
              </div>
            {:else if editingRuleType === "max_count"}
              <div class="form-group">
                <span class="form-label">Categories</span>
                <CategoryPicker
                  {generationId}
                  mode="multi"
                  selected={editingParams.categories || []}
                  on:change={({ detail }) => onParamsChange("categories", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  Rows in any of these categories count toward the cap below.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-max">Max Count</label>
                <input id="rule-param-max" class="form-input" type="number" min="1" bind:value={editingParams.max} />
                <p class="text-xs text-neutral-500 mt-1">
                  The maximum number of rows allowed, counted per the Count scope setting below.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-sc">Count scope</label>
                <select id="rule-param-sc" class="form-input" bind:value={editingParams.scope}>
                  <option value="product">product</option>
                  <option value="section">section</option>
                </select>
                <p class="text-xs text-neutral-500 mt-1">
                  "product" caps each individual product separately; "section" caps the total across all selected categories combined.
                </p>
              </div>
            {:else if editingRuleType === "ratio"}
              <div class="form-group">
                <span class="form-label">Categories</span>
                <CategoryPicker
                  {generationId}
                  mode="multi"
                  selected={editingParams.categories || []}
                  on:change={({ detail }) => onParamsChange("categories", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  Rows in any of these categories are added together and compared against the total row count.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-max-ratio">Max Ratio (0–1)</label>
                <input id="rule-param-max-ratio" class="form-input" type="number" min="0" max="1" step="0.1" bind:value={editingParams.max} />
                <p class="text-xs text-neutral-500 mt-1">
                  A fraction of ALL spending rows in the generation, e.g. 0.3 = 30%. Once the selected categories reach this share, further rows in them are rejected.
                </p>
              </div>
            {:else if editingRuleType === "followed_by"}
              <div class="form-group">
                <span class="form-label">From Category</span>
                <CategoryPicker
                  {generationId}
                  mode="single"
                  selected={editingParams.from ?? null}
                  on:change={({ detail }) => onParamsChange("from", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  The category that triggers the requirement — once a row lands here, a row in "To Category" is required afterward.
                </p>
              </div>
              <div class="form-group">
                <span class="form-label">To Category</span>
                <CategoryPicker
                  {generationId}
                  mode="single"
                  selected={editingParams.to ?? null}
                  on:change={({ detail }) => onParamsChange("to", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  The category that must eventually follow a "From Category" row. Checked at completion time, not row-by-row.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-within">Within Minutes</label>
                <input id="rule-param-within" class="form-input" type="number" min="1" bind:value={editingParams.within_minutes} placeholder="Unlimited" />
                <p class="text-xs text-neutral-500 mt-1">
                  Recorded but NOT enforced by the engine today — the check is purely categorical (from → to, no time window). Leave blank; filling it in has no effect on generation.
                </p>
              </div>
            {:else if editingRuleType === "section_order"}
              <div class="form-group">
                <span class="form-label">Categories (in order)</span>
                <CategoryPicker
                  {generationId}
                  mode="multi"
                  orderable={true}
                  selected={editingParams.categories || []}
                  on:change={({ detail }) => onParamsChange("categories", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  Checking a category adds it to the end of the order below. Use the "Selected order" panel's ↑/↓ buttons (or drag rows) to reorder — that order is exactly what gets saved and enforced. Checked at completion time.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-mode">Mode</label>
                <select id="rule-param-mode" class="form-input" bind:value={editingParams.mode} data-testid="rule-param-mode">
                  <option value="strict">strict</option>
                  <option value="gate">gate</option>
                </select>
                <p class="text-xs text-neutral-500 mt-1">
                  "strict" requires the categories back-to-back in exactly this order; "gate" allows other categories to appear in between, as long as relative order holds.
                </p>
              </div>
            {:else if editingRuleType === "required_spending_rows"}
              <div class="form-group">
                <label class="form-label" for="rule-param-value">Required row count</label>
                <input id="rule-param-value" class="form-input" type="number" min="0" data-testid="rule-param-value" bind:value={editingParams.value} />
                <p class="text-xs text-neutral-500 mt-1">
                  The generation must end with exactly this many spending rows. Top-up rows are read-only and are never counted toward this total. Checked by verify_generation and complete_generation, not per-row.
                </p>
              </div>
            {:else if editingRuleType === "required_final_balance"}
              <div class="form-group">
                <label class="form-label" for="rule-param-value">Target final balance</label>
                <input id="rule-param-value" class="form-input" type="number" step="any" data-testid="rule-param-value" bind:value={editingParams.value} />
                <p class="text-xs text-neutral-500 mt-1">
                  The balance the generation should end at, in the project's currency units.
                </p>
              </div>
              <div class="form-group">
                <label class="flex items-center gap-2 cursor-pointer text-sm text-neutral-800">
                  <input type="checkbox" class="form-checkbox" data-testid="rule-param-exact" bind:checked={editingParams.exact} />
                  Exact
                </label>
                <p class="text-xs text-neutral-500 mt-1">
                  This target balance is only actually enforced when this box is checked. Unchecked, it is advisory only — nothing is validated against it at completion.
                </p>
              </div>
            {:else if editingRuleType === "required_final_row"}
              <div class="form-group">
                <label class="form-label" for="rule-param-description">Description</label>
                <input id="rule-param-description" class="form-input" type="text" data-testid="rule-param-description" bind:value={editingParams.description} placeholder="e.g. Battle joined" />
                <p class="text-xs text-neutral-500 mt-1">
                  The last row's description must match this text exactly. Leave blank to not check the description.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-amount">Amount</label>
                <input id="rule-param-amount" class="form-input" type="number" step="any" data-testid="rule-param-amount" bind:value={editingParams.amount} placeholder="e.g. 47" />
                <p class="text-xs text-neutral-500 mt-1">
                  The last row's amount, in the project's currency units. Leave blank to not check the amount.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-amount-eur">Amount EUR</label>
                <input id="rule-param-amount-eur" class="form-input" type="number" step="any" data-testid="rule-param-amount-eur" bind:value={editingParams.amount_eur} placeholder="e.g. 4.70" />
                <p class="text-xs text-neutral-500 mt-1">
                  The last row's EUR-denominated amount. Leave blank to not check it.
                </p>
              </div>
            {:else if editingRuleType === "min_time_span_hours"}
              <div class="form-group">
                <label class="form-label" for="rule-param-value">Hours</label>
                <input id="rule-param-value" class="form-input" type="number" min="0" step="any" data-testid="rule-param-value" bind:value={editingParams.value} />
                <p class="text-xs text-neutral-500 mt-1">
                  The first and last spending rows must be at least this many hours apart by timestamp. Top-up rows aren't spending rows and don't count as endpoints.
                </p>
              </div>
            {:else if editingRuleType === "required_top_up_window_depletion"}
              <div class="form-group">
                <label class="form-label" for="rule-param-max-balance">Max balance before next top-up</label>
                <input id="rule-param-max-balance" class="form-input" type="number" step="any" data-testid="rule-param-max-balance" bind:value={editingParams.max_balance_before_next_top_up} />
                <p class="text-xs text-neutral-500 mt-1">
                  For each qualifying gap between top-ups (see below), the balance must be spent down to this ceiling or lower before the next top-up lands.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-min-window">Min window minutes</label>
                <input id="rule-param-min-window" class="form-input" type="number" min="0" data-testid="rule-param-min-window" bind:value={editingParams.min_window_minutes} />
                <p class="text-xs text-neutral-500 mt-1">
                  Only gaps between top-ups of at least this many minutes are checked — short back-to-back top-ups are exempt.
                </p>
              </div>
            {:else if editingRuleType === "category_consistency"}
              <div class="form-group">
                <label class="flex items-center gap-2 cursor-pointer text-sm text-neutral-800">
                  <input type="checkbox" class="form-checkbox" data-testid="rule-param-enabled" bind:checked={editingParams.enabled} />
                  Enabled
                </label>
                <p class="text-xs text-neutral-500 mt-1">
                  When enabled, any row in one of the categories below must carry an amount and amount_eur that agree at the project's currency rate (within rounding); a mismatched row is rejected at post time. Uncheck to disable the check entirely for this generation.
                </p>
              </div>
              <div class="form-group">
                <span class="form-label">Categories</span>
                <CategoryPicker
                  {generationId}
                  mode="multi"
                  selected={editingParams.categories || []}
                  on:change={({ detail }) => onParamsChange("categories", detail)}
                  on:ready={({ detail }) => onPickerReady(detail)}
                />
                <p class="text-xs text-neutral-500 mt-1">
                  The categories this consistency check applies to. Leave empty to apply the check to all categories.
                </p>
              </div>
            {:else if editingRuleType === "time_realism"}
              <div class="form-group">
                <label class="flex items-center gap-2 cursor-pointer text-sm text-neutral-800">
                  <input type="checkbox" class="form-checkbox" data-testid="rule-param-enabled" bind:checked={editingParams.enabled} />
                  Enabled
                </label>
                <p class="text-xs text-neutral-500 mt-1">
                  This check is ON by default for every generation — you only need this rule to loosen it or switch it off. Unchecking lets the agent post clock-round, evenly-spaced timestamps again.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-minute-multiple">Minute boundary</label>
                <input id="rule-param-minute-multiple" class="form-input" type="number" min="1" data-testid="rule-param-minute-multiple" bind:value={editingParams.minute_multiple} />
                <p class="text-xs text-neutral-500 mt-1">
                  A timestamp is refused when its seconds are :00 <em>and</em> its minutes land on this boundary — 5 refuses 09:15:00 and 09:30:00. Both halves must be round, so 09:17:00 and 09:15:22 are always fine. Raise it to allow more round-looking times, lower it to allow fewer.
                </p>
              </div>
              <div class="form-group">
                <label class="form-label" for="rule-param-max-uniform-gaps">Max repeated gaps</label>
                <input id="rule-param-max-uniform-gaps" class="form-input" type="number" min="1" data-testid="rule-param-max-uniform-gaps" bind:value={editingParams.max_uniform_gaps} />
                <p class="text-xs text-neutral-500 mt-1">
                  Refuses a row whose gap from the previous one repeats this many earlier gaps exactly — 2 means three identical gaps in a row are rejected, which is what stops the agent marching the timeline forward by a fixed step.
                </p>
              </div>
            {/if}

            <div class="flex gap-2 justify-end pt-1">
              <button class="btn-secondary text-xs" data-testid="rule-cancel" on:click={cancelEdit}>Cancel</button>
              <button class="btn-primary text-xs" data-testid="rule-save" on:click={saveRule} disabled={saving || (formHasPicker && !pickerReady)}>
                {saving ? "Saving..." : (formHasPicker && !pickerReady) ? "Loading…" : editingRuleId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        {/if}
      {/if}
    </div>

    <!-- RIGHT: the same read-only "rules in words" list as the rail (Part A),
         reused here so a create/edit is visible without leaving the page. -->
    <div class="lg:col-span-2 min-w-0">
      <h3 class="text-xs font-bold text-neutral-700 tracking-wider mb-2 uppercase">Current rules</h3>
      {#if error}
        <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1 text-xs mb-3" role="alert">{error}</p>
      {:else if loading}
        <p class="text-neutral-500 text-xs">Loading rules...</p>
      {:else}
        {#if defaultRules.length > 0}
          <button
            type="button"
            class="w-full flex items-center gap-2 text-left mb-2.5 px-2.5 py-1.5 rounded-md border border-dashed border-neutral-300 bg-neutral-50 hover:bg-neutral-100 transition-colors"
            data-testid="rules-defaults-toggle"
            aria-expanded={showDefaults}
            title="Enforced on every generation with no rule record behind them. Add a rule of the same type to change or switch one off."
            on:click={() => (showDefaults = !showDefaults)}
          >
            <span class="text-[0.68rem] text-neutral-500">{showDefaults ? "▾" : "▸"}</span>
            <span class="text-xs font-bold text-neutral-700">🔒 Always on</span>
            <span class="text-[0.68rem] text-neutral-500">{defaultRules.length} built-in {defaultRules.length === 1 ? "policy" : "policies"}, applied to every generation</span>
          </button>
          {#if showDefaults}
            <ul class="space-y-2.5 mb-2.5" data-testid="rules-defaults-list">
              {#each defaultRules as rule (ruleKey(rule))}
                <li
                  class="border border-dashed border-neutral-300 rounded-md p-3 bg-neutral-50"
                  data-testid="rule-item"
                  data-rule-source={rule.source}
                >
                  <div class="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span class="text-xs font-bold text-neutral-900">{rule.display?.title || ruleLabel(rule)}</span>
                    <span
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-neutral-200 text-neutral-700 border border-neutral-300"
                      data-testid="rule-default-badge"
                      title="Always on for every generation. There is no rule record behind this — it is the built-in default."
                    >🔒 always on</span>
                  </div>
                  {#if rule.display?.summary}
                    <p class="text-xs text-neutral-600 mb-1.5">{rule.display.summary}</p>
                  {/if}
                  {#if rule.display?.params?.length > 0}
                    <table class="w-full text-[0.7rem] text-neutral-700">
                      <tbody>
                        {#each rule.display.params as field}
                          <tr>
                            <td class="pr-2 py-0.5 align-top font-semibold text-neutral-500 whitespace-nowrap">{field.label}</td>
                            <td class="py-0.5 align-top break-words">{field.value}</td>
                          </tr>
                        {/each}
                      </tbody>
                    </table>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        {/if}

        {#if ownRules.length === 0}
          <p class="text-neutral-500 text-xs mb-3" data-testid="rules-empty">
            No rules of your own for this generation.
          </p>
        {/if}
        <ul class="space-y-2.5">
          {#each ownRules as rule, index (ruleKey(rule))}
            <li
              class="border rounded-md p-3 transition-colors border-neutral-200 hover:border-neutral-300 {rule.source === 'project' ? 'opacity-70 bg-neutral-50/50' : 'bg-white'}"
              data-testid="rule-item"
              data-rule-source={rule.source}
              data-rule-id={rule.id}
            >
              <div class="flex items-center gap-2 mb-1.5 flex-wrap">
                <span class="text-xs font-bold text-neutral-900">{rule.display?.title || ruleLabel(rule)}</span>
                {#if rule.source === "project"}
                  <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-info-bg text-info-text border border-info-border/20">project</span>
                {:else}
                  <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-success-bg text-success-text border border-success-border/20">generation</span>
                {/if}
                {#if rule.scope}
                  <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-neutral-100 text-neutral-600 ml-auto">{rule.scope}</span>
                {/if}
              </div>
              {#if rule.display}
                {#if rule.display.summary}
                  <p class="text-xs text-neutral-600 mb-1.5">{rule.display.summary}</p>
                {/if}
                {#if rule.display.params && rule.display.params.length > 0}
                  <table class="w-full text-[0.7rem] text-neutral-700 mb-1.5">
                    <tbody>
                      {#each rule.display.params as field}
                        <tr>
                          <td class="pr-2 py-0.5 align-top font-semibold text-neutral-500 whitespace-nowrap">{field.label}</td>
                          <td class="py-0.5 align-top break-words">{field.value}</td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                {/if}
              {:else}
                <p class="text-xs text-neutral-500 mb-1.5">{ruleDescription(rule)}</p>
                {#if !rule.params || Object.keys(rule.params).length === 0}
                  <span class="text-[0.68rem] text-neutral-400 italic block mb-1.5">No parameters required</span>
                {/if}
              {/if}

              {#if isAdmin}
                <button
                  type="button"
                  class="text-[0.66rem] text-neutral-400 hover:text-neutral-600 underline mb-1.5"
                  data-testid="rule-show-raw-toggle"
                  on:click={() => toggleRaw(ruleKey(rule))}
                >
                  {showRawIds.has(ruleKey(rule)) ? "Hide raw" : "Show raw"}
                </button>
                {#if showRawIds.has(ruleKey(rule))}
                  <pre class="text-[0.7rem] bg-neutral-50 border border-neutral-200/60 rounded p-2 overflow-x-auto font-mono whitespace-pre-wrap break-all text-neutral-800 mb-1.5" data-testid="rule-raw-json">{JSON.stringify(rule.params, null, 2)}</pre>
                {/if}
              {/if}

              {#if rule.source === "generation" && isAdmin}
                <div class="flex gap-2 justify-end mt-2">
                  <button class="btn-secondary py-0.5 px-2 text-[0.72rem]" data-testid="rule-edit" on:click={() => startEdit(rule)}>Edit</button>
                  <button class="btn-danger py-0.5 px-2 text-[0.72rem] bg-danger-bg text-danger-text border border-danger-border/20" on:click={() => deleteRule(rule)}>Delete</button>
                </div>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </div>
</div>
